import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Search, Eye, UserCog, Trash2, Loader2, Users2, Mail, Phone,
  Building2, User,
} from "lucide-react";
import ClienteDialog from "@/pages/modules/clientes/ClienteDialog";

const TIPO_ID_LABEL = {
  cedula: "Cédula", dimex: "DIMEX", cedula_juridica: "Céd. Jurídica", pasaporte: "Pasaporte",
};

export default function Clientes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["super_admin", "admin", "abogado"].includes(user?.role);
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterTipo, setFilterTipo] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () =>
    api.get("/clientes")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Error cargando clientes"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const del = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/clientes/${deleteTarget.id}`);
      toast.success("Cliente eliminado");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const filtered = items.filter((c) => {
    if (filterTipo !== "todos" && c.tipo !== filterTipo) return false;
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return [c.nombre, c.identificacion, c.email, c.telefono]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="space-y-8" data-testid="module-clientes">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Módulo</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-[#1A0810]">Clientes</h1>
          <p className="mt-2 text-sm text-[#4a3838] max-w-2xl">
            Registro completo de personas físicas y jurídicas del bufete.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="rounded-md bg-white border border-[#E5DFD3] px-4 py-2 text-sm text-[#1A0810]">
            <span className="text-[#6B5D57] mr-1">Total:</span>
            <span className="font-display text-base">{items.length}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-[280px]">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
            <Input
              data-testid="clientes-search"
              placeholder="Buscar por nombre, identificación, correo..."
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="h-11 pl-10 bg-white border-[#E5DFD3] focus-visible:ring-[#7A1E2E]"
            />
          </div>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger data-testid="clientes-filter-tipo" className="h-11 w-[180px] bg-white border-[#E5DFD3]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los tipos</SelectItem>
              <SelectItem value="fisica">Persona física</SelectItem>
              <SelectItem value="juridica">Persona jurídica</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="clientes-add-button"
            className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Agregar cliente
          </Button>
        )}
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E] mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Users2 className="h-10 w-10 text-[#C19B6C] mx-auto" strokeWidth={1.3} />
            <p className="mt-4 text-sm text-[#6B5D57]">
              {items.length === 0 ? "Aún no hay clientes registrados." : "Sin resultados para su búsqueda."}
            </p>
            {items.length === 0 && canEdit && (
              <Button
                onClick={() => { setEditing(null); setDialogOpen(true); }}
                className="mt-6 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar primer cliente
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                <TableHead className="text-[#3d2a2a] font-semibold">Cliente</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Identificación</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Contacto</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`cliente-row-${c.id}`}
                  className="border-b border-[#E5DFD3] hover:bg-[#FBF9F4] cursor-pointer"
                  onClick={() => navigate(`/clientes/${c.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 ring-1 ring-[#E5DFD3]">
                        <AvatarFallback className={`text-xs font-semibold ${c.tipo === "juridica" ? "bg-[#C19B6C] text-[#2B0913]" : "bg-[#7A1E2E] text-[#F1EDE4]"}`}>
                          {c.tipo === "juridica" ? <Building2 className="h-4 w-4" strokeWidth={1.8} /> : initials(c.nombre)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm font-medium text-[#1A0810]">{c.nombre}</div>
                        <div className="text-xs text-[#6B5D57] mt-0.5 flex items-center gap-1">
                          {c.tipo === "juridica" ? <Building2 className="h-3 w-3" strokeWidth={1.8} /> : <User className="h-3 w-3" strokeWidth={1.8} />}
                          {c.tipo === "juridica" ? "Persona jurídica" : "Persona física"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#1A0810]">{c.identificacion || "—"}</div>
                    <div className="text-[11px] uppercase tracking-widest text-[#6B5D57] mt-0.5">
                      {TIPO_ID_LABEL[c.tipo_identificacion] || c.tipo_identificacion}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs text-[#3d2a2a] space-y-0.5">
                      {c.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3 text-[#6B5D57]" strokeWidth={1.8} />{c.email}</div>}
                      {c.telefono && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-[#6B5D57]" strokeWidth={1.8} />{c.telefono}</div>}
                      {!c.email && !c.telefono && <span className="text-[#6B5D57]">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.activo ? (
                      <Badge className="bg-[#EEF6EE] text-[#1F5B2E] hover:bg-[#EEF6EE] border border-[#1F5B2E]/20">Activo</Badge>
                    ) : (
                      <Badge className="bg-[#F5EEE0] text-[#6B5D57] hover:bg-[#F5EEE0] border border-[#E5DFD3]">Inactivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => navigate(`/clientes/${c.id}`)}
                        data-testid={`cliente-view-${c.id}`}
                        className="h-8 w-8 hover:bg-[#F5EEE0]"
                        title="Ver cliente"
                      >
                        <Eye className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                      </Button>
                      {canEdit && (
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => { setEditing(c); setDialogOpen(true); }}
                          data-testid={`cliente-edit-${c.id}`}
                          className="h-8 w-8 hover:bg-[#F5EEE0]"
                          title="Editar"
                        >
                          <UserCog className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => setDeleteTarget(c)}
                          data-testid={`cliente-delete-${c.id}`}
                          className="h-8 w-8 hover:bg-[#FBEDEF]"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ClienteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => { setDialogOpen(false); load(); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar cliente</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Está seguro de eliminar a <b>{deleteTarget?.nombre}</b>? Se eliminarán también todos sus procesos y cobros. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="cliente-confirm-delete" className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
